// Fake data for the systems PLAN-04 will build. One home, so PLAN-04 swaps a
// function here rather than touching a screen (PLAN-03 §4).
//
// Rules that keep this honest:
//   - every export is named `placeholderX` and carries the system it waits for;
//   - screens never import this file — they go through selectors, so a swap is
//     one function in one place;
//   - a dev-dashboard badge lists the live ones (see SELECTOR_PLACEHOLDERS).
//
// The season numbers this file used to hold (chick prices, the chick budget, the
// sexed terms, the bag sizes, the flock cap, the Veteran cooldown) are gone:
// they live in the season config and are read through `sim/season.ts` and
// `sim/spots.ts`, so a season locked in the admin panel is the one the screens
// charge (REFOCUS §11b). The hidden-stat reveal is gone for the same reason from
// the other side: `sim/access.ts` draws each bird's own reveal day.
//
// The breed profile and the eight close-up cards are gone too. Temperament is
// `sim/breedProfile.ts` reading the docility `sim/escape.ts` enforces, and the
// cards are `sim/inspect.ts` reading the bird's own conditions and meters. What
// neither could supply is a hardiness scale, so the breed card shows two bars
// rather than three — see `sim/breedProfile.ts` for why a third would be a lie.

import type { Breed, HousingType } from '../core/types';
import { BREEDS } from '../sim/config';
import { flockCapFor } from '../sim/spots';

/**
 * PLAN-01 C adds `buff_orpington` to the `Breed` union. Until that lands the
 * launch list is a string, and the buy screen skips the row rather than
 * showing a breed the sim has no numbers for (PLAN-03 §2).
 */
export type LaunchBreedId = Breed | 'buff_orpington';

/** The launch five (REFOCUS §6), in the order the mockup lists them. */
export const LAUNCH_BREEDS: LaunchBreedId[] = [
  'silkie',
  'australorp',
  'leghorn',
  'rhode_island_red',
  'buff_orpington',
];

export interface HousingCard {
  housing: HousingType;
  /**
   * Flock cap, read from the rule the sim enforces rather than typed here, so
   * the first screen a player sees cannot promise a flock the season does not
   * allow (REFOCUS §11a). Normal, because Veteran is chosen a screen later.
   */
  maxHens: number;
  /** i18n keys for the chips under the name. */
  chips: string[];
  coopSprite: string;
}

export const HOUSING_CARDS: HousingCard[] = [
  {
    housing: 'backyard',
    maxHens: flockCapFor('backyard', false),
    // No predator chip: `sim/predators.ts` picks the roster by region, not by
    // housing, so a card promising "more predators" here promises a difference
    // the sim does not make.
    chips: ['home.backyard.run'],
    coopSprite: 'struct_coop_backyard',
  },
  {
    housing: 'balcony',
    maxHens: flockCapFor('balcony', false),
    chips: ['home.balcony.neighbors', 'home.balcony.litter'],
    coopSprite: 'struct_coop_balcony',
  },
];

/** Egg output bar, 0–100, from the real per-week rate in biology-config. */
export function breedEggBar(breed: Breed): number {
  // biology-config.json carries a `_source` note next to the profiles, so the
  // rates are read off the numeric entries only.
  const rates = Object.values(BREEDS)
    .map((profile) => profile?.eggRatePerWeek)
    .filter((rate): rate is number => typeof rate === 'number' && rate > 0);
  const best = Math.max(...rates);
  if (!best) return 0;
  return Math.round(((BREEDS[breed]?.eggRatePerWeek ?? 0) / best) * 100);
}

/**
 * Every placeholder the shell is still reading, for the dev-dashboard badge.
 * Keep this list in step with the exports above.
 */
export const SELECTOR_PLACEHOLDERS: Array<{ name: string; waitsFor: string }> = [
  { name: 'placeholderHousingCards', waitsFor: 'PLAN-04 — housing specs' },
  { name: 'placeholderYardSpots', waitsFor: 'PLAN-04 — spot layout' },
  { name: 'placeholderBinDaysLeft', waitsFor: 'PLAN-04 — bag spoilage' },
  { name: 'placeholderTameness', waitsFor: 'PLAN-04 — handling and tameness' },
  { name: 'placeholderPlanSpots', waitsFor: 'PLAN-04 — spot layout and items' },
  { name: 'placeholderAirTemp', waitsFor: 'PLAN-04 — league weather' },
  { name: 'placeholderRoofs', waitsFor: 'PLAN-04 — coop fixtures' },
  { name: 'placeholderVents', waitsFor: 'PLAN-04 — coop fixtures' },
  { name: 'placeholderInsideSpots', waitsFor: 'PLAN-04 — inside spots' },
  { name: 'placeholderBroodyChoices', waitsFor: 'PLAN-04 — hatching and broody breaker' },
  { name: 'placeholderBroodyCosts', waitsFor: 'PLAN-04 — hatching and broody breaker' },
];

// --- U3: the coop screen ---------------------------------------------------

/** Below this, a vital is a warning (REFOCUS §11c: distress, not a percentage). */
export const WARNING_WORST_ACCEPTABLE = 60;

export interface SpotMarker {
  /** 1-based, the number the diorama and the spot rows share. */
  n: number;
  /** i18n key for the spot's name, e.g. `setup.spot.waterer`. */
  nameKey: string;
  /** 0–1 across the diorama box. */
  x: number;
  /** 0–1 down the diorama box. */
  y: number;
}

/**
 * Where the numbered spot markers sit on the diorama.
 *
 * PLAN-04: replace with the sim's real spot layout. Until then these are the
 * mockup's five backyard positions, so the overlay has something honest to show
 * and the Setup screen has rows to fill.
 */
export function placeholderYardSpots(housing: HousingType): SpotMarker[] {
  if (housing === 'balcony') {
    return [
      { n: 1, nameKey: 'setup.spot.railing', x: 0.14, y: 0.56 },
      { n: 2, nameKey: 'setup.spot.wall', x: 0.36, y: 0.5 },
      { n: 3, nameKey: 'setup.spot.overhang', x: 0.66, y: 0.48 },
      { n: 4, nameKey: 'setup.spot.door', x: 0.86, y: 0.6 },
    ];
  }
  // The mockup's five backyard positions, pulled onto the ground the fitted
  // camera actually draws (the iso grid is wider than the mockup's render).
  return [
    { n: 1, nameKey: 'setup.spot.waterer', x: 0.14, y: 0.56 },
    { n: 2, nameKey: 'setup.spot.feeder', x: 0.5, y: 0.52 },
    { n: 3, nameKey: 'setup.spot.coveredFeeder', x: 0.86, y: 0.58 },
    { n: 4, nameKey: 'setup.spot.waterer2', x: 0.3, y: 0.68 },
    { n: 5, nameKey: 'setup.spot.dustBath', x: 0.72, y: 0.68 },
  ];
}

/** How much is left in the open bin, in days. */
export function placeholderBinDaysLeft(): number {
  return 1;
}

// --- U5: the bird and warning screens --------------------------------------

/** Tameness, 0–100. PLAN-04: replaced by the handling model (BHV-15). */
export function placeholderTameness(ageInDays: number, seed: number): number {
  return Math.max(0, Math.min(100, 20 + (seed % 30) + Math.floor(ageInDays / 12)));
}

/** Warnings before the sanctuary in Normal mode. REFOCUS §3. */
export const placeholderNormalWarningCount = 5;

/** Warnings before she is on her own in Veteran. REFOCUS §3. */
export const placeholderVeteranWarningCount = 2;

// --- U6: the setup plan and the broody screen ------------------------------

export interface PlanSpot {
  n: number;
  /** i18n key for the spot's name. */
  nameKey: string;
  /** i18n key for what is in it. */
  itemKey: string;
  /** Plan view coordinates (the 358x265 viewBox the mockup draws). */
  x: number;
  y: number;
  /** A waterer: its water temperature is the thing shade changes. */
  hasWater: boolean;
  /** i18n key for the extra line under the name, when there is one. */
  extraKey: string | null;
  /** The rodent zone runs along the fence, not at a spot. */
  rodent: boolean;
}

export const PLAN_VIEWBOX = { width: 358, height: 265 } as const;

/**
 * The spot list the plan is drawn from.
 *
 * PLAN-04: replace with the sim's real spot layout. These are the mockup's
 * five backyard spots and the four a balcony has (REFOCUS §7: backyard starts
 * with 5 and unlocks 8, balcony has 4 fixed spots).
 */
export function placeholderPlanSpots(housing: HousingType): PlanSpot[] {
  if (housing === 'balcony') {
    return [
      { n: 1, nameKey: 'setup.spot.railing', itemKey: 'setup.item.waterer', x: 40, y: 128, hasWater: true, extraKey: null, rodent: false },
      { n: 2, nameKey: 'setup.spot.wall', itemKey: 'setup.item.feedDish', x: 185, y: 190, hasWater: false, extraKey: 'setup.extra.bossGuard', rodent: false },
      { n: 3, nameKey: 'setup.spot.overhang', itemKey: 'setup.item.coveredFeeder', x: 318, y: 196, hasWater: false, extraKey: 'setup.extra.rodents', rodent: true },
      { n: 4, nameKey: 'setup.spot.door', itemKey: 'setup.item.waterer2', x: 104, y: 56, hasWater: true, extraKey: 'setup.extra.outOfView', rodent: false },
    ];
  }
  return [
    { n: 1, nameKey: 'setup.spot.waterer', itemKey: 'setup.item.waterer', x: 40, y: 128, hasWater: true, extraKey: null, rodent: false },
    { n: 2, nameKey: 'setup.spot.feeder', itemKey: 'setup.item.feedDish', x: 185, y: 190, hasWater: false, extraKey: 'setup.extra.bossGuard', rodent: false },
    { n: 3, nameKey: 'setup.spot.coveredFeeder', itemKey: 'setup.item.coveredFeeder', x: 318, y: 196, hasWater: false, extraKey: 'setup.extra.rodents', rodent: true },
    { n: 4, nameKey: 'setup.spot.waterer2', itemKey: 'setup.item.waterer2', x: 104, y: 56, hasWater: true, extraKey: 'setup.extra.outOfView', rodent: false },
    { n: 5, nameKey: 'setup.spot.dustBath', itemKey: 'setup.item.dustBath', x: 60, y: 222, hasWater: false, extraKey: 'setup.extra.drySoil', rodent: false },
  ];
}

/**
 * Air temperature by hour, the mockup's own table (a San Juan summer day).
 * PLAN-04: league weather supplies this.
 */
export function placeholderAirTemp(hour: number): number {
  const AIR: Record<number, number> = {
    6: 77, 7: 78, 8: 80, 9: 82, 10: 84, 11: 86,
    12: 87, 13: 88, 14: 88, 15: 87, 16: 85, 17: 83, 18: 81,
  };
  return AIR[hour] ?? 84;
}

/** Roof and ventilation choices for the coop tab. */
export interface CoopFixture {
  id: string;
  labelKey: string;
  /** i18n key for the effect line. */
  noteKey: string;
  cost: number;
}

export function placeholderRoofs(): CoopFixture[] {
  return [
    { id: 'tin', labelKey: 'setup.roof.tin', noteKey: 'setup.roof.tinNote', cost: 0 },
    { id: 'white', labelKey: 'setup.roof.white', noteKey: 'setup.roof.whiteNote', cost: 0 },
    { id: 'wood', labelKey: 'setup.roof.wood', noteKey: 'setup.roof.woodNote', cost: 12 },
  ];
}

export function placeholderVents(): CoopFixture[] {
  return [
    { id: 'none', labelKey: 'setup.vent.none', noteKey: 'setup.vent.noneNote', cost: 0 },
    { id: 'ridge', labelKey: 'setup.vent.ridge', noteKey: 'setup.vent.ridgeNote', cost: 8 },
    { id: 'fan', labelKey: 'setup.vent.fan', noteKey: 'setup.vent.fanNote', cost: 40 },
  ];
}

/** Inside the coop: nest box, the roost bar and the light (REFOCUS §7). */
export function placeholderInsideSpots(): PlanSpot[] {
  return [
    { n: 1, nameKey: 'setup.inside.corner', itemKey: 'setup.inside.nestBox', x: 40, y: 128, hasWater: false, extraKey: null, rodent: false },
    { n: 2, nameKey: 'setup.inside.door', itemKey: 'setup.inside.floor', x: 185, y: 190, hasWater: false, extraKey: 'setup.inside.floorNote', rodent: false },
    { n: 3, nameKey: 'setup.inside.roost', itemKey: 'setup.inside.light', x: 104, y: 56, hasWater: false, extraKey: 'setup.inside.lightNote', rodent: false },
  ];
}

// --- the broody screen (U6) -------------------------------------------------

export interface BroodyChoice {
  id: 'hatch' | 'incubator' | 'break';
  labelKey: string;
  noteKey: string;
  cost: number;
  /** i18n key for the primary button when this choice is picked. */
  actionKey: string;
}

export function placeholderBroodyChoices(): BroodyChoice[] {
  return [
    { id: 'hatch', labelKey: 'broody.hatch', noteKey: 'broody.hatchNote', cost: 12, actionKey: 'broody.buyFertileEggs' },
    { id: 'incubator', labelKey: 'broody.incubator', noteKey: 'broody.incubatorNote', cost: 240, actionKey: 'broody.buyIncubator' },
    { id: 'break', labelKey: 'broody.break', noteKey: 'broody.breakNote', cost: 0, actionKey: 'broody.breakBroodiness' },
  ];
}

/** What broodiness costs her, REFOCUS §12b. */
export function placeholderBroodyCosts(incubator: boolean): string[] {
  if (incubator) {
    return ['broody.costIncubator1', 'broody.costIncubator2', 'broody.costIncubator3'];
  }
  return ['broody.costHen1', 'broody.costHen2', 'broody.costHen3'];
}
